"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CurrencyInput } from "@/components/ui/currency-input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { usePostAlert } from "@/queries/mutations/use-post-alert";
import { useDeleteAlert } from "@/queries/mutations/use-delete-alert";
import { usePatchAlert } from "@/queries/mutations/use-patch-alert";

import { modalitySearchParam, paymentSearchParam } from "@/filters";
import {
  areaSizes,
  modalityCriteria,
  paymentCriteria,
  typeCriteria,
} from "@/constants";

import states from "@/static/states.json";
import { RequiredBuyPremiumButton } from "./required/buy-premium-button";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Confirmation } from "./confirmation";

export type AlertFormValues = z.infer<typeof formSchema>;

export type BlockType =
  | "default"
  | "cities"
  | "address"
  | "neighborhood"
  | "area"
  | "auctionDate"
  | "type"
  | "modality"
  | "payment"
  | "price"
  | "discount";

interface Props {
  id?: string;
  data?: AlertFormValues;
  onClose?: () => void;
}

type FormData = {
  blockType: string;
  titulo: string;
  email: string;
  telefone: string;
  uf: string;
  cidades?: string | null;
  endereco?: string | null;
  bairros?: string | null;
  area?: number | null;
  dias_faltantes_leilao?: number | null;
  tipo?: string | null;
  modalidade_de_venda: string[];
  pagamento: string[];
  preco_min?: number | null;
  preco_max?: number | null;
  desconto?: number | null;
};

type BlockConfig = {
  label: string;
  value: BlockType;
  image: string;
  description: string;
  field: keyof FormValues;
  component: (props: { control: any; defaultValue?: any }) => React.ReactNode;
  validate?: (data: FormData) => boolean;
  message?: string;
  path?: string[];
};

const blocks: Record<Exclude<BlockType, "default">, BlockConfig> = {
  cities: {
    label: "Cidades",
    value: "cities",
    image: "/alerts/alert-cities.png",
    description: "Filtre por cidades específicas.",
    field: "cidades",
    validate: (data) => !!data.cidades,
    message: "Informe as cidades",
    path: ["cidades"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="cidades"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Cidades{" "}
              <span className="text-xs text-muted-foreground">
                (separe as cidades por vírgula)
              </span>
            </FormLabel>
            <Input
              placeholder="ex: Osasco, Ribeirão Preto, São Carlos"
              defaultValue={field.value || defaultValue || ""}
              onChange={field.onChange}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  address: {
    label: "Endereço",
    value: "address",
    image: "/alerts/alert-address.png",
    description: "Filtre por rua, número e etc.",
    field: "endereco",
    validate: (data) => !!data.endereco,
    message: "Informe o endereço",
    path: ["endereco"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="endereco"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Endereço</FormLabel>
            <Input
              placeholder="ex: Rua das Flores, 123"
              defaultValue={field.value || defaultValue || ""}
              onChange={field.onChange}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  neighborhood: {
    label: "Bairro",
    value: "neighborhood",
    image: "/alerts/alert-neighborhood.png",
    description: "Filtre por bairros.",
    field: "bairros",
    validate: (data) => !!data.bairros,
    message: "Informe o bairro",
    path: ["bairros"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="bairros"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Bairros{" "}
              <span className="text-xs text-muted-foreground">
                (separe os bairros por vírgula)
              </span>
            </FormLabel>
            <Input
              placeholder="ex: Vila Nova, Jardim das Flores"
              defaultValue={field.value || defaultValue || ""}
              onChange={field.onChange}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  area: {
    label: "Área",
    value: "area",
    image: "/alerts/alert-area.png",
    description: "Filtre por m² mínimo e máximo.",
    field: "area",
    validate: (data) => !!data.area,
    message: "Informe a área",
    path: ["area"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="area"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Área (m²)</FormLabel>
            <Select
              onValueChange={(value) => field.onChange(parseInt(value))}
              defaultValue={
                field.value?.toString() || defaultValue?.toString() || "-1"
              }
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a área" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {areaSizes.map((size) => {
                  const value = size.toString();

                  return (
                    <SelectItem key={value} value={value}>
                      +{value}m²
                    </SelectItem>
                  );
                })}
                <SelectItem value="-1">Sem máximo</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  auctionDate: {
    label: "Data da leilão",
    value: "auctionDate",
    image: "/alerts/alert-auction-date.png",
    description: "Filtre por dias faltantes para o leilão.",
    field: "dias_faltantes_leilao",
    validate: (data) => !!data.dias_faltantes_leilao,
    message: "Informe a quantidade de dias faltantes para o leilão",
    path: ["dias_faltantes_leilao"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="dias_faltantes_leilao"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Dias faltantes para o leilão</FormLabel>
            <Input
              type="number"
              placeholder="ex: 10"
              defaultValue={field.value || defaultValue || ""}
              onChange={(e) => {
                const value = e.target.valueAsNumber;
                field.onChange(value);
              }}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  type: {
    label: "Tipo",
    value: "type",
    image: "/alerts/alert-type.png",
    description: "Filtre por apartamento, casa e etc.",
    field: "tipo",
    validate: (data) => !!data.tipo,
    message: "Informe o tipo do imóvel",
    path: ["tipo"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="tipo"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tipo do Imóvel</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value || defaultValue || undefined}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {Object.entries(typeCriteria).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  modality: {
    label: "Modalidade",
    value: "modality",
    image: "/alerts/alert-modality.png",
    description: "Filtre por leilão SFI, venda direta e etc.",
    field: "modalidade_de_venda",
    validate: (data) => data.modalidade_de_venda.length > 0,
    message: "Selecione pelo menos uma modalidade",
    path: ["modalidade_de_venda"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="modalidade_de_venda"
        render={() => (
          <FormItem>
            <FormLabel>Modalidade</FormLabel>
            <div className="flex flex-row gap-4 flex-wrap">
              {modalitySearchParam.map((key) => (
                <FormField
                  key={key}
                  control={control}
                  name="modalidade_de_venda"
                  render={({ field }) => (
                    <FormItem
                      key={key}
                      className="flex flex-row items-center gap-2 space-y-0"
                    >
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(key)}
                          onCheckedChange={(checked) => {
                            return checked
                              ? field.onChange([...field.value, key])
                              : field.onChange(
                                  field.value?.filter(
                                    (value: string) => value !== key
                                  )
                                );
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">
                        {modalityCriteria[key]}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  payment: {
    label: "Pagamento",
    value: "payment",
    image: "/alerts/alert-payment.png",
    description: "Filtre por FGTS, financiamento e etc.",
    field: "pagamento",
    validate: (data) => data.pagamento.length > 0,
    message: "Selecione pelo menos uma forma de pagamento",
    path: ["pagamento"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="pagamento"
        render={() => (
          <FormItem>
            <FormLabel>Pagamento</FormLabel>
            <div className="flex flex-row gap-4 flex-wrap">
              {paymentSearchParam.map((key) => (
                <FormField
                  key={key}
                  control={control}
                  name="pagamento"
                  render={({ field }) => (
                    <FormItem
                      key={key}
                      className="flex flex-row items-center gap-2 space-y-0"
                    >
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(key)}
                          onCheckedChange={(checked) => {
                            return checked
                              ? field.onChange([...field.value, key])
                              : field.onChange(
                                  field.value?.filter(
                                    (value: string) => value !== key
                                  )
                                );
                          }}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">
                        {paymentCriteria[key]}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
  price: {
    label: "Preço",
    value: "price",
    image: "/alerts/alert-price.png",
    description: "Filtre por preço mínimo e máximo.",
    field: "preco_min",
    validate: (data) => !!data.preco_min || !!data.preco_max,
    message: "Informe pelo menos um preço (mínimo ou máximo)",
    path: ["preco_min"],
    component: ({ control, defaultValue }) => (
      <div className="flex flex-row gap-2">
        <FormField
          control={control}
          name="preco_min"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Preço mínimo</FormLabel>
              <CurrencyInput
                className="col-span-2 h-8"
                placeholder="ex: R$ 100.000"
                onValueChange={(_, __, values) => {
                  field.onChange(values?.float);
                }}
                defaultValue={field.value || defaultValue?.min || undefined}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="preco_max"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Preço máximo</FormLabel>
              <CurrencyInput
                className="col-span-2 h-8"
                placeholder="ex: R$ 300.000"
                onValueChange={(_, __, values) => {
                  field.onChange(values?.float);
                }}
                defaultValue={field.value || defaultValue?.max || undefined}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    ),
  },
  discount: {
    label: "Desconto",
    value: "discount",
    image: "/alerts/alert-discount.png",
    description: "Filtre por desconto mínimo.",
    field: "desconto",
    validate: (data) => !!data.desconto,
    message: "Informe o desconto mínimo",
    path: ["desconto"],
    component: ({ control, defaultValue }) => (
      <FormField
        control={control}
        name="desconto"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Desconto mínimo (%)</FormLabel>
            <Input
              type="number"
              placeholder="ex: 20"
              defaultValue={field.value || defaultValue || ""}
              onChange={(e) => {
                const value = e.target.valueAsNumber;
                field.onChange(value);
              }}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    ),
  },
};

const blockTypes = [
  {
    label: "Padrão",
    description: "Filtre por todos os critérios.",
    value: "default",
    image: "/alerts/alert-default.png",
  },
  ...Object.values(blocks),
] satisfies {
  label: string;
  value: BlockType;
  image: string;
  description: string;
}[];

const formSchema = z
  .object({
    blockType: z.string(),
    titulo: z
      .string({
        required_error: "Informe um título para o seu alerta",
      })
      .trim()
      .min(1, "Informe um título para o seu alerta"),
    email: z
      .string({
        required_error: "Informe o seu email",
      })
      .trim()
      .email("Formato de email inválido")
      .min(1, "Informe o seu email"),
    telefone: z.string().trim(),
    uf: z
      .string({
        required_error: "Informe o estado",
      })
      .trim()
      .min(1, "Informe o estado"),
    cidades: z.string().nullable(),
    endereco: z.string().nullable(),
    bairros: z.string().nullable(),
    area: z.number().nullable(),
    dias_faltantes_leilao: z.number().nullable(),
    tipo: z.string().nullable(),
    modalidade_de_venda: z.array(z.string()).default([]),
    pagamento: z.array(z.string()).default([]),
    preco_min: z.number().nullable(),
    preco_max: z.number().nullable(),
    desconto: z.number().nullable(),
  })
  .refine(
    (data) => {
      const block = blocks[data.blockType as Exclude<BlockType, "default">];
      if (!block?.validate) return true;
      return block.validate(data);
    },
    (data) => {
      const block = blocks[data.blockType as Exclude<BlockType, "default">];
      return {
        message: block?.message || "Validação falhou",
        path: block?.path || ["blockType"],
      };
    }
  );

type FormValues = z.infer<typeof formSchema>;

export const AlertForm = ({ id, data }: Props) => {
  const [selectedBlockType, setSelectedBlockType] = useState<BlockType | null>(
    () => (id ? "default" : null)
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const router = useRouter();

  const postMutation = usePostAlert();
  const patchMutation = usePatchAlert();
  const deleteMutation = useDeleteAlert();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      blockType: selectedBlockType || "",
      titulo: data?.titulo || "",
      email: data?.email || "",
      telefone: data?.telefone || "",
      uf: data?.uf || "",
      cidades: data?.cidades || null,
      endereco: data?.endereco || null,
      bairros: data?.bairros || null,
      area: data?.area || null,
      dias_faltantes_leilao: data?.dias_faltantes_leilao || null,
      tipo: data?.tipo || null,
      modalidade_de_venda: data?.modalidade_de_venda || [],
      pagamento: data?.pagamento || [],
      preco_min: data?.preco_min || null,
      preco_max: data?.preco_max || null,
      desconto: data?.desconto || null,
    },
  });

  const close = () => {
    router.back();
    form.reset();
  };

  const remove = async (id: string) => {
    setIsDeleting(true);

    await deleteMutation.mutateAsync(id, {
      onSuccess: () => {
        setIsDeleting(false);
        close();
      },
    });
  };

  const submit = async (data: FormValues) => {
    if (id) {
      await patchMutation.mutateAsync(
        {
          ...data,
          id,
          area: data.area === -1 ? null : data.area ? data.area : null,
          modalidade_de_venda: String(data.modalidade_de_venda),
          pagamento: String(data.pagamento),
        },
        {
          onSuccess: close,
        }
      );
    } else {
      await postMutation.mutateAsync(
        {
          ...data,
          area:
            // @ts-ignore
            parseFloat(data.area) === -1 ? null : data.area ? data.area : null,
          modalidade_de_venda: String(data.modalidade_de_venda),
          pagamento: String(data.pagamento),
        },
        {
          onSuccess: close,
        }
      );
    }
  };

  if (!selectedBlockType) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {blockTypes.map((type) => (
          <Card
            key={type.value}
            className={cn(
              "p-4 cursor-pointer hover:border-primary transition-colors",
              "flex items-center justify-between gap-2"
            )}
            onClick={() => setSelectedBlockType(type.value)}
          >
            <div className="flex items-start gap-4 h-full">
              <div className="rounded-md flex items-center justify-center shrink-0">
                <div className="w-7 h-7 bg-muted rounded-full flex items-center justify-center">
                  <img
                    loading="eager"
                    src={type.image}
                    alt={type.label}
                    className="w-4 h-4 object-contain"
                  />
                </div>
              </div>
              <div className="flex flex-col items-start gap-2">
                <h3 className="text-base font-medium">{type.label}</h3>
                <p className="text-sm text-muted-foreground">
                  {type.description}
                </p>
              </div>
            </div>
            <div>
              <ArrowRightIcon className="w-3 h-3 text-muted-foreground" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit(submit)}
      >
        <div className="space-y-2 w-full">
          <FormField
            control={form.control}
            name="titulo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Título</FormLabel>
                <Input
                  placeholder={`ex: Alerta de apartamentos em ${
                    data?.uf.toUpperCase() || "SP"
                  }`}
                  defaultValue={field.value}
                  onChange={field.onChange}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-row gap-2">
          <div className="space-y-2 w-full">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    defaultValue={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="space-y-2 w-full">
            <FormField
              control={form.control}
              name="telefone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <Input
                    type="tel"
                    placeholder="(00) 00000-0000"
                    defaultValue={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="space-y-2 w-full">
          <FormField
            control={form.control}
            name="uf"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estado do Imóvel</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o estado" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {selectedBlockType && selectedBlockType !== "default" && (
          <div className="space-y-2">
            {blocks[selectedBlockType].component({
              control: form.control,
              defaultValue: data?.[blocks[selectedBlockType].field],
            })}
          </div>
        )}
        <Accordion
          type="single"
          defaultValue={id ? "optional-fields" : undefined}
          collapsible
          className="w-full"
        >
          <AccordionItem value="optional-fields">
            <AccordionTrigger>
              <span>
                Campos Opcionais{" "}
                <span className="text-xs text-muted-foreground">
                  (clique para expandir)
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-neutral-800">
              <div className="space-y-6 px-[4px]">
                {Object.entries(blocks).map(([key, block]) => {
                  if (key === selectedBlockType) return null;
                  return (
                    <div key={key} className="space-y-2">
                      {block.component({
                        control: form.control,
                        defaultValue: data?.[block.field],
                      })}
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="mt-4 flex flex-row items-center justify-between gap-8">
          {id ? (
            <Confirmation
              title="Remover alerta"
              description="Tem certeza que deseja remover este alerta?"
              confirm={{
                disabled: isDeleting,
                label: isDeleting ? (
                  <>
                    <Spinner inverse /> Removendo
                  </>
                ) : (
                  "Continuar"
                ),
                onClick: () => remove(id),
              }}
            >
              <Button
                variant="secondary"
                className="w-full flex flex-row gap-2 items-center justify-center"
              >
                Remover alerta
              </Button>
            </Confirmation>
          ) : (
            <div className="flex flex-row gap-2 flex-start w-full">
              <Button
                type="reset"
                variant="secondary"
                className="w-full"
                onClick={() => {
                  setSelectedBlockType(null);
                }}
              >
                Voltar
              </Button>
              <Button
                type="reset"
                variant="outline"
                className="w-full"
                onClick={() => {
                  form.reset();
                }}
              >
                Limpar
              </Button>
            </div>
          )}
          <RequiredBuyPremiumButton>
            <Button
              type="submit"
              disabled={postMutation.isPending || patchMutation.isPending}
              className="w-full flex flex-row gap-2 items-center justify-center"
            >
              {postMutation.isPending || patchMutation.isPending ? (
                <>
                  <Spinner inverse /> Salvando
                </>
              ) : (
                "Salvar alerta"
              )}
            </Button>
          </RequiredBuyPremiumButton>
        </div>
      </form>
    </Form>
  );
};
